import React, { useEffect, useState } from 'react';

import { useConfiguration } from '../../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  PROFILE_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
  isNotFoundError,
} from '../../../util/errors';
import { hasPermissionToViewData, isUserAuthorized } from '../../../util/userHelpers';

import { Page, NamedRedirect } from '../../../components';

import TopbarContainer from '../../TopbarContainer/TopbarContainer';
import FooterContainer from '../../FooterContainer/FooterContainer';
import NotFoundPage from '../../NotFoundPage/NotFoundPage';

import ProfileHeaderCard from './ProfileHeaderCard';
import ProfileListingsSection from './ProfileListingsSection';
import ProfileReviewsSection from './ProfileReviewsSection';
import { useCustomProfilePage } from '../../../hooks/useCustomProfilePage';

type RouteParams = { id?: string; slug?: string; variant?: string };

type CustomProfilePageProps = {
  params: RouteParams;
  location?: { pathname: string; search: string; hash: string };
  staticContext?: any;
};

const CustomProfilePage = (props: CustomProfilePageProps) => {
  const { params: pathParams, location, staticContext } = props;
  const config = useConfiguration() as any;
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    scrollingDisabled,
    currentUser,
    user,
    useCurrentUser,
    userShowError,
    queryListingsError,
    listings,
    reviews,
    queryReviewsError,
  } = useCustomProfilePage(pathParams.id);

  const isVariant = (pathParams.variant?.length ?? 0) > 0;
  const isPreview =
    isVariant && pathParams.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;

  // Stripe's onboarding hits the public profile of every seller. If the
  // visitor is the Stripe crawler (mode=storefront) and the seller hasn't
  // built much yet, fall back to the landing page.
  const searchParams = location?.search;
  const parsedQuery = searchParams ? new URLSearchParams(searchParams) : null;
  const isStorefront = parsedQuery?.get('mode') === 'storefront';
  // Design-preview escape hatch: render Sharetribe-shaped mock reviews
  // even when the marketplace has no real provider reviews.
  const previewMockReviews = parsedQuery?.get('previewMockReviews') === '1';
  if (isStorefront) {
    return <NamedRedirect name="LandingPage" />;
  }

  const isCurrentUser =
    !!currentUser?.id?.uuid && currentUser?.id?.uuid === pathParams.id;
  const profileUser = useCurrentUser ? currentUser : user;
  const profile = profileUser?.attributes?.profile || {};
  const { bio, displayName } = profile;

  const isPrivateMarketplace = config?.accessControl?.marketplace?.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const isUnauthorizedOnPrivateMarketplace = isPrivateMarketplace && isUnauthorizedUser;
  const hasUserPendingApprovalError = isErrorUserPendingApproval(userShowError);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasNoViewingRightsOnPrivateMarketplace =
    isPrivateMarketplace && hasNoViewingRightsUser;

  const isDataLoaded = isPreview
    ? currentUser != null || userShowError != null
    : hasNoViewingRightsOnPrivateMarketplace
    ? currentUser != null || userShowError != null
    : user != null || userShowError != null;

  const schemaTitleVars = {
    name: displayName,
    marketplaceName: config?.marketplaceName,
  };
  const schemaTitle = intl.formatMessage(
    { id: 'CustomProfilePage.schemaTitle' },
    schemaTitleVars
  );

  if (!isDataLoaded) {
    return null;
  }
  if (!isPreview && isNotFoundError(userShowError)) {
    return <NotFoundPage staticContext={staticContext} />;
  }
  if (!isPreview && (isUnauthorizedOnPrivateMarketplace || hasUserPendingApprovalError)) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  }
  if (
    (!isPreview && hasNoViewingRightsOnPrivateMarketplace && !isCurrentUser) ||
    isErrorNoViewingPermission(userShowError)
  ) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }
  if (!isPreview && isForbiddenError(userShowError)) {
    return (
      <NamedRedirect
        name="SignupPage"
        state={{
          from: `${location?.pathname || ''}${location?.search || ''}${location?.hash || ''}`,
        }}
      />
    );
  }
  if (isPreview && mounted && !isCurrentUser) {
    return isCurrentUser === false ? (
      <NamedRedirect name="ProfilePage" params={{ id: currentUser?.id?.uuid }} />
    ) : null;
  }
  if ((isPreview || isPrivateMarketplace) && !mounted) {
    return null;
  }

  const showLoadingFailed = userShowError || queryListingsError;

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        mainEntity: {
          '@type': 'Person',
          name: displayName,
        },
        name: schemaTitle,
      }}
    >
      <TopbarContainer />

      <div className="flex w-full flex-grow flex-col bg-[#fcfaf6]">
        <main className="container mx-auto flex-grow px-4 py-5 md:py-6">
          <div className="mx-auto max-w-6xl space-y-5">
            {showLoadingFailed ? (
              <p className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                <FormattedMessage id="ProfilePage.loadingDataFailed" />
              </p>
            ) : (
              <>
                <ProfileHeaderCard
                  user={profileUser}
                  displayName={displayName ?? null}
                  bio={bio ?? null}
                />
                <ProfileListingsSection listings={listings} profileUser={profileUser} />
                <ProfileReviewsSection
                  reviews={reviews}
                  queryReviewsError={queryReviewsError}
                  previewMockReviews={previewMockReviews}
                />
              </>
            )}
          </div>
        </main>
      </div>

      <FooterContainer />
    </Page>
  );
};

export default CustomProfilePage;
